import { dayjs } from '../../support/helpers/dayjs.ts';
import { APIClient } from '@autopylot-internal/autopylot-api-client';
import { MissionFeatureCollectionFacadeAdapter, PersistableMissionFeatureCollection } from '@autopylot-internal/tiles-client';
import { State } from '@autopylot-internal/state';
import { AlertPresenter } from '../../app/AlertBinder.tsx';
import { HistoryViewModel } from '../../app/HistoryViewModel.ts';
import { IPath, PagePathsBuilder } from '../../app/PagePathsBuilder.ts';
import { MapViewModel } from '../../map/model/MapViewModel.ts';
import { EditableMissionState, INewMission, IMission, IMissionCallbacks } from '../../mission/model/EditableMissionState.ts';
import { IPersistableMission } from './IPersistableMission.ts';
import { IMissionCreationSource, NewMissionState } from './NewMissionState.ts';
import { OperatorState } from '../../profile/OperatorState.ts';
import { FAAVersionAlertManager } from '../../app/FAAVersionAlertManager.ts';
import { TrackingService } from '../../support/tracking/TrackingService.ts';
import { UserFacingError } from '../../support/UserFacingError.ts';
import { Logger } from '../../support/debug/Logger.ts';
import { GlobalCachingConfig } from '../../GlobalCachingConfig.ts';
import { StorageProvider, IAsyncSmartStorageProvider } from '../../support/StorageProvider.ts';
import { MissionPinAndRadiusOnMapViewModel } from '../../map/mission-pin-and-radius/MissionPinAndRadiusOnMapViewModel.ts';
import { FleetManagementState } from '../../fleet/FleetManagementState.ts';
import { DeferredPromise } from '../../support/DeferredPromise.ts';

const FEET_PER_METER = 3.28084;

// for android the limit is Java String ~1.4 MB)
// and unlimited in iOS (other than iOS TV https://stackoverflow.com/questions/7510123/is-there-any-limit-in-storing-values-in-nsuserdefaults)
// at 20KB per mission (best guess), means we can hold 50 future missions before busting the cache
const MAX_CACHE_SIZE = /* 1 MB */ 1 * 1024 * 1024;

export const MAX_RECENTS = 20;

export type IMissionListSection = 'past' | 'canceled' | 'invalid';

interface IMissionsCache {
    missions: IPersistableMission[];
}

export class MissionsStateCache {
    static inject = () => [GlobalCachingConfig, StorageProvider];
    constructor(private globalConfig: GlobalCachingConfig, storage: StorageProvider) {
        this.cache = storage.getAsyncJSONProvider<IMissionsCache>('MissionsState.v1', { maxBytes: MAX_CACHE_SIZE, removeOnTooBig: true });
    }

    private cache: IAsyncSmartStorageProvider<IMissionsCache>;

    get = () => {
        if (this.globalConfig.disabled){
            throw new Error('caching is disabled');
        }
        return this.cache.get();
    };

    set = async (value: IMissionsCache) => {
        if (this.globalConfig.disabled){
            return;
        }
        this.cache.set(value);
    };

    removeMissionFromCache = async ({ missionId }: { missionId: string }) => {
        try{
            let { missions } = (await this.get())!;
            missions = missions.filter(m => m.missionId !== missionId);
            this.set({ missions });
        }
        catch (err: any){

        }
    };

    upsertMissionInCache = async (updated: IPersistableMission) => {
        try{
            let { missions } = (await this.get())!;
            missions = missions.filter(m => m.missionId !== updated.missionId).concat(updated);
            this.set({ missions });
        }
        catch (err: any){

        }
    };
}

interface IMissionsState {
    lastLoaded: number;
    isFullyLoaded: boolean;
    missions: EditableMissionState[];
    expandedSection?: IMissionListSection;
}

export class MissionsState extends State<IMissionsState> {

    static inject = () => [
        MapViewModel,
        HistoryViewModel,
        PagePathsBuilder,
        APIClient,
        MissionFeatureCollectionFacadeAdapter,
        AlertPresenter,
        OperatorState,
        FAAVersionAlertManager,
        TrackingService,
        MissionsStateCache,
        Logger,
        MissionPinAndRadiusOnMapViewModel,
        FleetManagementState
    ];
    constructor(
        private mapViewModel: MapViewModel,
        private history: HistoryViewModel,
        private pagePaths: PagePathsBuilder,
        private apiClient: APIClient,
        private missionFeatureCollectionFacadeAdapter: MissionFeatureCollectionFacadeAdapter,
        private alertPresenter: AlertPresenter,
        private operator: OperatorState,
        private versionAlertManager: FAAVersionAlertManager,
        private tracker: TrackingService,
        private cache: MissionsStateCache,
        private logger: Logger,
        private pinAndRadius: MissionPinAndRadiusOnMapViewModel,
        private fleetManagementState: FleetManagementState
    ) {
        super({
            lastLoaded: Date.now(),
            isFullyLoaded: false,
            missions: []
        });

        this.pages = {
            new: this.pagePaths.build<{dropPin?: string}>((context) => `#/map${context?.dropPin ? `?dropPin=${context.dropPin}` : ''}`)
        };
    }

    private fullyLoadedPromise = new DeferredPromise<boolean>();

    private missionCallbacks: IMissionCallbacks = {
        onNameChanged: async (_mission: IMission) => {
            const updated = await this.apiClient.patch(_mission.links.self, { name: _mission.name, version: _mission.version }, { accept: 'application/vnd.autopylot.mission.v2+json' });

            await this.refreshMissionState(updated);

            this.setState({});
        },

        onNotesHtmlChanged: async (_mission: IMission) => {
            const updated = await this.apiClient.patch(_mission.links.self, { notesHtml: _mission.notesHtml, version: _mission.version }, { accept: 'application/vnd.autopylot.mission.v2+json' });

            await this.refreshMissionState(updated);
            this.setState({});
        },

        onStatusChanged: async (_mission: IMission) => {
            const updated = await this.apiClient.patch(_mission.links.self, { status: _mission.status, version: _mission.version }, { accept: 'application/vnd.autopylot.mission.v2+json' });

            if (_mission.status === 'user-canceled'){
                this.fleetManagementState.refresh(); // don't await
            }

            await this.refreshMissionState(updated);

            this.setState({});
        },

        onDeleted: async (_mission: IMission) => {
            await this.apiClient.delete(_mission);

            this.fleetManagementState.refresh(); // don't await

            this.removeMissionFromState(_mission);
        },

        onRefresh: async (_mission: IMission) => {
            const mission = await this.apiClient.get(_mission.links.self, 'application/vnd.autopylot.mission.v2+json');

            this.cache.upsertMissionInCache(mission);

            this.deserializeIntoState([mission]);
        },

        getConcurrentLAANCMissions: (_mission: Partial<IMission>) => {
            const { startTime, durationMinutes } = _mission;
            const startMoment = dayjs(startTime);
            const endMoment = dayjs(startTime).add(durationMinutes!, 'minutes');
            return this.state.missions
                .filter(m => {
                    if (m.state.missionId === _mission.missionId) {
                        return false;
                    }
                    if (!m.getMissionRadius()?.getLAANCCompositeAdvisory()) {
                        return false;
                    }
                    if (m.getMissionStatus() !== 'active') {
                        return false;
                    }
                    if (!startMoment.isBefore(m.getEndTime()) || !endMoment.isAfter(m.getStartTime())) {
                        return false;
                    }
                    return true;
                })
                .map(m => m.state);
        }
    };

    private injectDependencies = (mission: NewMissionState | EditableMissionState) => {
        mission.mapViewModel = this.mapViewModel;
        mission.history = this.history;
        mission._paths = this.pagePaths;
        mission.alertPresenter = this.alertPresenter;
        mission.operator = this.operator;
        mission.versionAlertManager = this.versionAlertManager;
        mission.tracker = this.tracker;
        mission.pinAndRadius = this.pinAndRadius;
        mission.onInjected();
    };

    getMissionById = async (missionId: string): Promise<EditableMissionState> => {
        let mission = this.state.missions.find(m => m.state.missionId ===  missionId);

        if (!mission) {
            await this.waitUntilFullyLoaded();
            mission = this.state.missions.find(m => m.state.missionId ===  missionId);
        }

        if (!mission) {
            throw new UserFacingError({
                status: 404,
                errorCode: 'mission.not-found',
                message: `mission ${missionId} wasn't found among ${this.state.missions.length} missions`,
                displayMessage: 'Sorry, this mission does not exist, or has been deleted.'
            });
        }

        return mission;
    };

    getReminderCount = () => {
        return this.state.missions.reduce((total, m) => total + (m.getReminderCount() ? 1 : 0), 0);
    };

    hasPendingInvalidatedMissions = () => {
        return this.state.missions.some(m => m.getMissionStatus() === 'active' && m.getAPLAANCStatus() !== 'authorizing-failed' && m.getCanFly() === 'cannot-fly');
    };

    /**
     * Returns recent (up to 20) missions suitable for the "Recents" section.
     * Excludes canceled and invalid missions, sorted by startTime descending (most recent first).
     */
    getRecentMissions = (): EditableMissionState[] => {
        return this.state.missions
            .filter(m => {
                const status = m.getMissionStatus();
                return status !== 'user-canceled' && !m.isNotValid();
            })
            .sort((a, b) => {
                const aEpoch = dayjs(a.getStartTime()).valueOf();
                const bEpoch = dayjs(b.getStartTime()).valueOf();
                return bEpoch - aEpoch; // most recent first
            })
            .slice(0, MAX_RECENTS);
    };

    beginCreatingMission = ({ trackingFrom }: { trackingFrom: IMissionCreationSource }) => {
        if (this.hasPendingInvalidatedMissions()){
            throw new UserFacingError({
                errorCode: 'invalidated-missions',
                displayMessage: 'There are mission(s) that must be canceled before you can create any new missions, per the FAA'
            });
        }

        const onSave = async (_new: INewMission) => {
            const entry = await this.apiClient.entry();
            const center = _new.radiusPolygon.getMissionCenter();
            const radiusMeters = _new.radiusPolygon.getMissionRadius().properties.radiusMeters;
            const _mission = await this.apiClient.post(entry.links.missions, {
                ..._new,
                radiusPolygon: undefined,
                center,
                radiusFeet: Math.round(radiusMeters * FEET_PER_METER),
                isSquare: _new.radiusPolygon.getMissionRadius().properties.isSquare
            }, { accept: 'application/vnd.autopylot.mission.v2+json' });

            this.fleetManagementState.assignDefaultAircraftToNewMission(_mission);

            this.cache.upsertMissionInCache(_mission);

            _mission.radiusPolygon = this.missionFeatureCollectionFacadeAdapter.adapt(new PersistableMissionFeatureCollection(_mission.radiusPolygon));

            const mission = this.addMissionToState(_mission);

            this.history.push(`#/missions/${mission.state.missionId}?alert=true&backTo=/missions`);
        };

        const { getConcurrentLAANCMissions } = this.missionCallbacks;

        const newMission = new NewMissionState({ onSave, getConcurrentLAANCMissions }, trackingFrom);
        this.injectDependencies(newMission);

        return newMission;
    };

    addMissionToState = (_mission: IMission) => {
        const mission = new EditableMissionState(_mission, this.missionCallbacks);
        this.injectDependencies(mission);

        this.setState({
            missions: this.state.missions?.concat(mission)
        });

        return mission;
    };

    removeMissionFromState = (_mission: IMission) => {
        this.setState({
            missions: this.state.missions.filter(m => m.state.startTime !== _mission.startTime)
        });
        this.cache.removeMissionFromCache(_mission);
    };

    refreshMissionState = async (_updated: IPersistableMission) => {
        const { radiusPolygon, ...rest } = _updated;

        const mission = await this.getMissionById(_updated.missionId);

        if (!mission) {
            throw new Error(`missionId ${_updated.missionId} not found`);
        }

        this.cache.upsertMissionInCache(_updated);

        mission.setState({
            radiusPolygon: this.missionFeatureCollectionFacadeAdapter.adaptFromPersisted(radiusPolygon),
            ...rest
        });
    };

    waitUntilFullyLoaded = async () => {
        return this.fullyLoadedPromise;
    };

    // load or reload all missions
    loadAll = async () => {
        const entry = await this.apiClient.entry();

        const allMissionIds: string[] = [];

        let page = await this.apiClient.get(entry.links.missions, 'application/vnd.autopylot.mission.v3+json');
        allMissionIds.push(...page.missions.map((m: any) => m.missionId));
        this.deserializeIntoState(page.missions);

        while (page.nextMissionId) {
            page = await this.apiClient.get(entry.links.missions, 'application/vnd.autopylot.mission.v3+json', { nextMissionId: page.nextMissionId });
            allMissionIds.push(...page.missions.map((m: any) => m.missionId));
            this.deserializeIntoState(page.missions);
        }

        // remove any that no longer exist (presumably deleted on other devices)
        // (IMPORTANT: iterate in reverse so splice doesn't break indexer)
        for (let i = this.state.missions.length - 1; i >= 0; i--) {
            const missionId = this.state.missions[i].state.missionId;
            if (!allMissionIds.includes(missionId)) {
                this.state.missions.splice(i, 1);
                this.cache.removeMissionFromCache({ missionId });
            }
        }

        this.setState({
            lastLoaded: Date.now(),
            isFullyLoaded: true
        });

        this.fullyLoadedPromise.resolve(true);

        this.fleetManagementState.refresh(); // don't await
    };

    // load or reload only recent and upcoming missions
    private loadRecentAndUpcomingFromRemote = async () => {

        // on app start and timed refreshes, we load upcoming plus 7 days of past missions, so that:
        // - the status is updated on missions that have completed since the last refresh
        // - accordion sections are partially loaded when they are first expanded (a nicer U/X)
        const daysInPast = 7;

        const entry = await this.apiClient.entry();
        const { missions: _missions } = await this.apiClient.get(entry.links.missions, 'application/vnd.autopylot.mission.v3+json', { daysInPast });

        this.cache.set({ missions: _missions });

        this.deserializeIntoState(_missions);
    };

    loadRecentAndUpcoming = async () => {
        try{
            const { missions } = (await this.cache.get())!;
            this.deserializeIntoState(missions);
            Promise.resolve()
                .then(async () => {
                    await this.loadRecentAndUpcomingFromRemote();
                })
                .catch((err: any) => {
                    const error: any = new Error(`MissionsState.loadRecentAndUpcoming() failed, but used cache. <Inner>${err.toString()}</Inner>`);
                    error.inner = error;
                    error.errorCode = error.inner.errorCode;

                    this.logger.error(error);
                });

        } catch (err: any){
            console.log('Mission state, cache miss', err);
            await this.loadRecentAndUpcomingFromRemote();
        }
    };

    // Update list state, and mission state of all missions in the list
    private deserializeIntoState = (serializable: IPersistableMission[]) => {
        const missions = this.state.missions;

        for (const m of serializable) {
            // turn the serialized radiusPolygon into the rich MissionFeatureCollectionFacade
            const radiusPolygon = this.missionFeatureCollectionFacadeAdapter.adapt(new PersistableMissionFeatureCollection(m.radiusPolygon));

            // since this can be called multiple times, detect existing mission state and update in place, to avoid orphans
            let mission = this.state.missions.find(e => e.state.missionId === m.missionId);

            if (mission) {
                mission.refreshState({
                    ...m,
                    radiusPolygon
                });
            } else {
                mission = new EditableMissionState({
                    ...m,
                    radiusPolygon
                }, this.missionCallbacks);
                this.injectDependencies(mission);
                missions.push(mission);
            }
        };

        this.setState({ lastLoaded: Date.now() });
    };

    pages: {
        new: IPath<{dropPin?: string}>
    };
}
